import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { Layout } from "@/components/layout/Layout";
import type { PillarPage } from "@/data/content-types";
import {
  applyPageSeo,
  appStoreUrlFor,
  breadcrumbLd,
  faqLd,
  mobileAppLd,
  organizationLd,
  receiptExamplesLd,
  removeJsonLd,
  upsertJsonLd,
  webPageLd,
  SITE_URL,
} from "@/lib/seo";
import { MERCHANT_NAMES } from "@/data/merchants";
import {
  Breadcrumbs,
  ComparisonGrid,
  CtaBand,
  FaqAccordion,
  MatrixGrid,
  Prose,
  RelatedLinks,
  Sections,
} from "./blocks";

const ui = {
  fr: { home: "Accueil", free: "Gratuit à télécharger. Disponible sur iPhone et Android." },
  en: { home: "Home", free: "Free to download. Available on iPhone and Android." },
};

export function ContentPage({ page }: { page: PillarPage }) {
  const { language } = useLanguage();
  const c = page[language];
  const path = language === "fr" ? page.frPath : page.enPath;
  const url = `${SITE_URL}${path}`;
  const t = ui[language];

  useEffect(() => {
    applyPageSeo({
      lang: language,
      title: c.metaTitle,
      description: c.metaDescription,
      keywords: c.keywords,
      path,
      frPath: page.frPath,
      enPath: page.enPath,
    });

    upsertJsonLd("organization", organizationLd());
    upsertJsonLd("app", mobileAppLd(language, c.metaDescription));
    upsertJsonLd("webpage", webPageLd({ lang: language, url, title: c.metaTitle, description: c.metaDescription }));
    upsertJsonLd(
      "breadcrumb",
      breadcrumbLd([
        { name: t.home, url: language === "fr" ? `${SITE_URL}/` : `${SITE_URL}/en` },
        { name: c.breadcrumb, url },
      ]),
    );
    upsertJsonLd("faq", faqLd(c.faq.map((f) => ({ q: f.q, a: f.a })), `${url}#faq`));

    // Merchant pages expose their visible "receipt line -> product" blocks as an
    // ItemList. Other content pages must not keep a stale one after client-side nav.
    const itemList = receiptExamplesLd({
      lang: language,
      url,
      merchant: MERCHANT_NAMES[page.key] ?? c.breadcrumb,
      sections: c.sections,
    });
    if (itemList) upsertJsonLd("itemlist", itemList);
    else removeJsonLd("itemlist");
  }, [language, c, path, url, page, t.home]);

  return (
    <Layout>
      <article className="relative">
        {/* Hero */}
        <section className="relative overflow-hidden noise-bg pt-28 pb-12 md:pt-36 md:pb-16">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 50% at 50% 0%, hsl(var(--primary) / 0.12) 0%, transparent 70%)",
            }}
          />
          <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
            <Breadcrumbs
              trail={[
                { label: t.home, href: language === "fr" ? "/" : "/en" },
                { label: c.breadcrumb },
              ]}
            />
            <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-primary mb-4">
              {c.eyebrow}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
              {c.h1}
            </h1>
            <div className="max-w-2xl">
              <Prose paragraphs={c.intro} />
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 sm:px-6 pb-16 space-y-14 md:space-y-16">
          <Sections sections={c.sections} />
          {c.matrix ? <MatrixGrid data={c.matrix} /> : null}
          {c.comparison ? <ComparisonGrid data={c.comparison} /> : null}
          <FaqAccordion title={c.faqTitle} items={c.faq} />
          <RelatedLinks title={c.relatedTitle} links={c.related} />
          <CtaBand
            title={c.ctaTitle}
            body={c.ctaBody}
            appStoreUrl={appStoreUrlFor(language)}
            freeLabel={t.free}
          />
        </div>
      </article>
    </Layout>
  );
}
